import { cn } from '@pesto/utils';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../DropdownMenu';
import { ScrollArea } from '../ScrollArea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../Tabs';

export type OverflowTabItem = {
  label: string;
  value: string;
  count?: number;
  component: ReactNode;
};

interface OverflowTabsProps {
  primary: OverflowTabItem[];
  more?: OverflowTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  moreLabel?: string;
  className?: string;
}

const CountBadge = ({ count, active }: { count: number; active?: boolean }) => (
  <span
    className={cn(
      'inline-flex h-5 min-w-5 items-center justify-center rounded border px-1.5 text-xs font-medium tabular-nums',
      active ? 'border-primary/20 bg-primary/10' : 'bg-muted',
    )}
  >
    {count}
  </span>
);

export const OverflowTabs = ({
  primary,
  more = [],
  value,
  onValueChange,
  moreLabel = 'More',
  className,
}: OverflowTabsProps) => {
  const allItems = useMemo(() => [...primary, ...more], [primary, more]);
  const activeMoreItem = more.find((item) => item.value === value);
  const moreActive = Boolean(activeMoreItem);
  const moreTriggerLabel = activeMoreItem?.label ?? moreLabel;

  return (
    <Tabs value={value} onValueChange={onValueChange} className={cn('space-y-4', className)}>
      <ScrollArea orientation="horizontal">
        <div className="bg-muted text-muted-foreground inline-flex h-10 items-center rounded-md p-1">
          <TabsList className="h-auto rounded-none bg-transparent p-0">
            {primary.map((item) => (
              <TabsTrigger key={item.value} value={item.value}>
                <span className="flex items-center gap-2">
                  {item.label}
                  {item.count !== undefined && item.count > 0 && (
                    <CountBadge count={item.count} active={item.value === value} />
                  )}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {more.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-state={moreActive ? 'active' : undefined}
                  className={cn(
                    'ring-offset-background focus-visible:ring-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                    moreActive
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <span>{moreTriggerLabel}</span>
                  {activeMoreItem?.count !== undefined && activeMoreItem.count > 0 && (
                    <CountBadge count={activeMoreItem.count} active />
                  )}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                {more.map((item) => {
                  const isActive = item.value === value;

                  return (
                    <DropdownMenuItem
                      key={item.value}
                      onSelect={() => onValueChange(item.value)}
                      className={cn(
                        'flex items-center justify-between gap-3',
                        isActive && 'bg-primary/10 text-foreground',
                      )}
                    >
                      <span>{item.label}</span>
                      {item.count !== undefined && item.count > 0 && <CountBadge count={item.count} />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </ScrollArea>

      <div className="min-w-0">
        {allItems.map((item) => (
          <TabsContent key={item.value} value={item.value}>
            {item.component}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
};
